import { useEffect, useRef, type ReactNode } from 'react';
import { EventBus } from '@campus/core';
import { useIsMobile } from '../hooks/useIsMobile.js';

/** Largeur des panneaux latéraux (chat, personnes) : le HUD à droite se décale d'autant. */
export const SIDE_PANEL_WIDTH = 360;

/**
 * Panneaux ouverts sur ordinateur. Compter plutôt que poser et retirer la
 * variable : en passant d'un panneau à l'autre (chat de réunion → messagerie),
 * le nouveau s'ouvre avant que l'ancien se ferme, et la fermeture de l'ancien
 * retirait --pl-side-panel alors qu'un panneau restait ouvert.
 */
let openPanels = 0;
function syncSidePanelWidth(): void {
  const root = document.documentElement;
  if (openPanels > 0) root.style.setProperty('--pl-side-panel', `${SIDE_PANEL_WIDTH}px`);
  else root.style.removeProperty('--pl-side-panel');
  EventBus.emit('ui:hud-insets-changed', {});
}

/** Champ où l'on tape du texte : les touches doivent y aller, pas au jeu. */
function isTextField(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

/**
 * Panneau pleine hauteur à droite (plein écran sur mobile). L'avatar reste
 * pilotable pendant qu'il est ouvert : le clavier du jeu n'est libéré
 * (ui:chat-open / ui:chat-close) que tant qu'un champ de texte du panneau a
 * le focus — écrire un message ou chercher une personne. --pl-side-panel est
 * posée sur <html> pour décaler le HUD. Un seul panneau à la fois (GameHUDToolbar).
 */
export function SidePanel({ label, onClose, header, children, footer }: {
  label: string;
  onClose: () => void;
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const isMobile = useIsMobile();

  // Panneau fermé pendant la saisie : le champ disparaît sans focusout fiable.
  const typingRef = useRef(false);
  const setTyping = (typing: boolean) => {
    if (typingRef.current === typing) return;
    typingRef.current = typing;
    try { EventBus.emit(typing ? 'ui:chat-open' : 'ui:chat-close', {}); } catch { /* */ }
  };
  useEffect(() => () => {
    if (typingRef.current) { typingRef.current = false; try { EventBus.emit('ui:chat-close', {}); } catch { /* */ } }
  }, []);

  // Clic hors du panneau (sur la carte) pendant la saisie : Phaser annule le
  // mousedown du canvas, le champ gardait donc le focus et le jeu restait sans
  // clavier jusqu'à un clic ailleurs dans le panneau. On quitte le champ.
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!typingRef.current || asideRef.current?.contains(e.target as Node)) return;
      const el = document.activeElement;
      if (el instanceof HTMLElement && asideRef.current?.contains(el)) el.blur();
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, []);

  useEffect(() => {
    if (isMobile) return;
    // Les vignettes vidéo se centrent à gauche du panneau (VideoTiles) ;
    // la caméra du jeu garde la carte visible à côté (ZoneScene).
    openPanels += 1;
    syncSidePanelWidth();
    return () => {
      openPanels -= 1;
      syncSidePanelWidth();
    };
  }, [isMobile]);

  return (
    <aside
      ref={asideRef}
      aria-label={label}
      style={{
        position: 'absolute',
        top: isMobile ? 'calc(40px + env(safe-area-inset-top, 0px))' : 0,
        right: 0, bottom: 0,
        width: isMobile ? '100%' : SIDE_PANEL_WIDTH,
        boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column',
        background: 'var(--color-bg-surface)',
        borderLeft: isMobile ? 'none' : '1px solid var(--color-border-default)',
        borderTop: isMobile ? '1px solid var(--color-border-default)' : 'none',
        borderRadius: isMobile ? 'var(--radius-xl) var(--radius-xl) 0 0' : 0,
        boxShadow: 'var(--shadow-lg)',
        pointerEvents: 'auto',
        zIndex: 'var(--z-panel)' as unknown as number,
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text-primary)',
      }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
      onFocus={(e) => setTyping(isTextField(e.target))}
      onBlur={(e) => { if (!isTextField(e.relatedTarget) || !e.currentTarget.contains(e.relatedTarget as Node)) setTyping(false); }}
    >
      {header}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{children}</div>
      {footer}
    </aside>
  );
}

export const panelHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  padding: '16px 12px 14px 20px',
  borderBottom: '1px solid var(--color-border-default)',
};
